import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.jsx'
import './index.css'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router'
import { QueryClientProvider, QueryClient, useQuery } from '@tanstack/react-query'
import { Login } from './pages/Login.jsx'
import { SelectShop } from './pages/SelectShop.jsx'
import { SelectExercise } from './pages/SelectExercise.jsx'
import { Button } from '@/components/ui/button'
import { Toaster } from 'react-hot-toast'
import { BtMenu } from './components/BtMenu.jsx'
import { Screensaver } from './components/Screensaver.jsx'
import { CartContext } from './context/cart.jsx'
import { UserContext } from './context/user.jsx'
import { Get } from './api/api.js'
import { Loading } from './components/Loading.jsx'
import { ConfirmDialogProvider } from './hooks/useConfirmDialog.jsx'
import { AlertDialogProvider } from './hooks/useAlertDialog.jsx'
import { clearForceReloadParam } from './lib/desktop.js'

// La actualización de Ajustes recarga con un parámetro en la URL para que
// Chromium no reutilice la caché. Se quita antes de pintar nada.
clearForceReloadParam();

const queryClient = new QueryClient()

import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://8a3d51f93cf4074ee0dcb74988120567@o4506509407289344.ingest.sentry.io/4506509418168320",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});


class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error: error };
  }

  componentDidCatch(error, errorInfo) {
    console.log(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className='fixed top-0 left-0 right-0 bottom-0 bg-neutral-800'>
          <div className='p-8 font-mono'>
            <div className='text-6xl text-left text-neutral-200'>Vaya :(</div>
            <div className='text-neutral-300 mt-4'>Ha ocurrido un error</div>
            <div className='p-4 my-2 rounded-md bg-black text-red-500'>
              <pre>{this.state.error.message}</pre>
            </div>
            <div className='text-neutral-300 mt-4'>Este error ha sido reportado a nuestros sistemas, de todos modos, puedes llamar a nuestro número de soporte 24/7 si necesitas asistencia.</div>
            <div className='text-emerald-300 text-lg'>Soporte Técnico 24/7: <strong>634 38 44 15</strong></div>
            <div className='fixed bottom-4 left-0 right-0 flex justify-center gap-6'>
              <Button size='lg' variant='default' onClick={() => window.location.href = '/'}>Volver al inicio</Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}


ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ConfirmDialogProvider>
          <AlertDialogProvider>
            <BrowserRouter>
              <Routes>
                <Route path="login" element={<Login />} />
                <Route path="*" element={
                  <ContextProvider>
                    <Routes>
                      <Route path="shopselect" element={<SelectShop />} />
                      <Route path="exerciseselect" element={<SelectExercise />} />
                      <Route path="*" element={
                        <div className="absolute top-0 left-0 bottom-0 right-0">
                          <div className="absolute top-0 left-0 right-0 bottom-0">
                            <App />
                          </div>
                          <BtMenu />
                           <Screensaver />
                        </div>
                      } />
                    </Routes>
                  </ContextProvider>
                } />
              </Routes>
            </BrowserRouter>
          </AlertDialogProvider>
        </ConfirmDialogProvider>
      </QueryClientProvider>
      <Toaster />
    </ErrorBoundary>
  </React.StrictMode>,
)

function ContextProvider({ children }) {
  const [cart, setCart] = React.useState([])
  const [comanda, setComanda] = React.useState(false)

  const { isLoading, data, error } = useQuery({
    queryKey: ["user", localStorage.getItem("token")],
    queryFn: () => Get({ request: "/user" }),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { isLoading: isLoadingShop, data: dataShop } = useQuery({
    queryKey: ["shop", localStorage.getItem("shop_id")],
    queryFn: () => Get({ request: `/shops/${localStorage.getItem("shop_id")}` }),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  if (isLoading || isLoadingShop) return <Loading />;
  if (error) return <Navigate to="/login" />;
  if (!data || data.code == 401 || !localStorage.getItem("token")) return <Navigate to="/login" />;

  return (
    <CartContext.Provider value={{ cart, setCart, comanda, setComanda }}>
      <UserContext.Provider value={{ user: data, shop: dataShop }}>
        {children}
      </UserContext.Provider>
    </CartContext.Provider>
  )
}
